<template>
  <GeojsonLayer
    :source="source"
    :sourceId="source.data.id"
    :layer="layerStyleMarker"
    @layerClicked="onComplexClicked"
    @layerMouseEnter="handleMouseEnterComplex"
    @layerMouseMove="handleMouseMoveComplex"
    @layerMouseLeave="handleMouseLeaveComplex"
  />
  <div v-show="false">
    <MapPopup ref="popup" :width="'300px'" :rows="rows" />
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapPopup from '@/components/map/popups/MapPopup.vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { mapState as mapStatePinia } from 'pinia'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { CHANGE_COMPLEX, COMPLEX_CLICKED } from '@/store/action-types'
import { SET_CLICKED_ITEM_TYPE, SET_HOVERED_COMPLEX_ID } from '@/store/mutation-types'
import { createDivider, createSmallSubtitle, createSmallTitle } from '@/components/Builder'
import { useIotCoverageStore } from '@/pinia/iotCoverage'
import useMapbox, { zoomToEntities } from '@/composables/useMapbox'
import iconSensor from '@/assets/sensor-icon.svg'
import iconIndoorView from '@/assets/icon-indoorview.svg'
export default {
  name: 'LayerComplexMarker',
  components: {
    GeojsonLayer,
    MapPopup
  },
  props: {
    layerId: {
      type: String,
      required: true
    },
    source: {
      validator: (prop) => typeof prop === 'object' || prop == null,
      default: null
    },
    buildingSource: {
      validator: (prop) => typeof prop === 'object' || prop == null,
      default: null
    }
  },
  emits: ['hover'],
  data() {
    return {
      sourceId: 'source-complex',
      popup: new mapboxgl.Popup({ closeButton: false, offset: 60 }),
      popupTitle: '',
      hoveredComplexId: '',
      buildingsBelongingToComplex: [],
      popupVisible: false,
      iconIndoorView,
      iconSensor
    }
  },
  computed: {
    ...mapStatePinia(useIotCoverageStore, {
      complexesWithIotData: 'complexes',
      constructionsWithIotData: 'constructions'
    }),
    ...mapStatePinia(useMapbox, {
      map: 'map'
    }),
    ...mapGetters({
      isMeasurementActive: 'isMeasurementActive'
    }),
    ...mapGetters(MAPVIEW_PREFIX, {
      selectedComplexName: 'selectedComplexName',
      selectedComplexId: 'selectedComplexId'
    }),
    ...mapState(MAPVIEW_PREFIX, {
      selectedRegionName: (state) => state.selectedRegionName,
      selectedComplex: (state) => state.selectedComplex
    }),
    ...mapState({
      realEstateFeatures: (state) => state.realEstateSource.data.features,
      complexFeatures: (state) => state.complexSource.data.features,
      clickedItemType: (state) => state.clickedItemType,
      mobileScreen: (state) => state.mobileScreen
    }),
    rows() {
      const hasSensorData = this.complexesWithIotData.includes(this.hoveredComplexId)
      const hasIndoorView = this.buildingsBelongingToComplex.some(
        (building) => building.navvisLinks.length > 0
      )
      const createIcon = (src, visible, margin) => ({
        type: 'image',
        style: {
          width: '20px',
          height: '25px',
          marginRight: margin ?? '10px',
          alignSelf: 'start',
          visibility: (visible === undefined ? true : visible) ? 'visible' : 'hidden'
        },
        props: {
          src
        }
      })
      return [
        createSmallTitle(this.popupTitle),
        ...(this.buildingsBelongingToComplex.length > 0 ? [createDivider()] : []),
        ...(this.buildingsBelongingToComplex.length > 0
          ? this.buildingsBelongingToComplex
              .slice()
              .sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
              .slice(0, 4)
              .map((building) => ({
                cols: [
                  {
                    type: 'builder',
                    style: {
                      flex: '1'
                    },
                    props: {
                      rows: [
                        createSmallTitle(building.displayName, {
                          style: {
                            width: '230px'
                          },
                          props: {
                            weight: 'regular'
                          }
                        }),
                        createSmallSubtitle(building.marking)
                      ]
                    }
                  },
                  createIcon(iconIndoorView, building.navvisLinks.length > 0),
                  createIcon(iconSensor, this.constructionsWithIotData.includes(building.id))
                ]
              }))
          : []),
        ...(this.buildingsBelongingToComplex.length > 3
          ? [
              createSmallTitle(this.$t('general__text__etc'), {
                style: {
                  width: '230px'
                },
                props: {
                  weight: 'regular'
                }
              })
            ]
          : []),
        ...(hasIndoorView || hasSensorData ? [createDivider()] : []),
        ...(hasIndoorView || hasSensorData
          ? [
              {
                style: {
                  justifyContent: 'center'
                },
                cols: [
                  ...(hasIndoorView
                    ? [
                        createIcon(iconIndoorView),
                        ...createSmallSubtitle(this.$t('general__view__indoorview'), {
                          style: {
                            alignSelf: 'center',
                            paddingRight: '10px'
                          }
                        }).cols
                      ]
                    : []),
                  ...(hasSensorData
                    ? [
                        createIcon(iconSensor),
                        ...createSmallSubtitle(this.$t('general__view__sensordata'), {
                          style: {
                            alignSelf: 'center'
                          }
                        }).cols
                      ]
                    : [])
                ]
              }
            ]
          : [])
      ]
    },
    layerStyleMarker() {
      return {
        id: this.layerId,
        source: this.source.data.id,
        type: 'symbol',
        maxzoom: 15,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': [
            'case',
            ['boolean', ['==', ['get', 'belongsToId'], this.hoveredComplexId], false],
            'complex_marker_shadow_active',
            ['boolean', this.isBuildingOrMoreDetailedSelected, true],
            'complex_marker_shadow_inactive',
            ['all', this.isAllComplexesSelected, true],
            'complex_marker_shadow_active',
            [
              'all',
              this.isComplexOrMoreDetailedSelected,
              ['!=', ['get', 'belongsToId'], this.selectedComplexId]
            ],
            'complex_marker_shadow_inactive',
            'complex_marker_shadow_active'
          ],
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom'
        },
        paint: {
          'icon-opacity': ['interpolate', ['linear'], ['zoom'], 14.5, 1, 15, 0]
        }
      }
    },
    isAllComplexesSelected() {
      return this.selectedComplexName === 'Alla' && this.clickedItemType === 'complex'
    },
    isComplexOrMoreDetailedSelected() {
      // Check for all types of (currently) possible markers
      return ['building', 'realEstate', 'complex'].indexOf(this.clickedItemType) !== -1
    },
    isBuildingOrMoreDetailedSelected() {
      return (
        ['room', 'poi', 'line', 'zone', 'level', 'building'].indexOf(this.clickedItemType) !== -1
      )
    }
  },

  methods: {
    ...mapActions('mapview', {
      complexClicked: COMPLEX_CLICKED
    }),
    ...mapActions({
      dispatchComplex: CHANGE_COMPLEX
    }),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE
    }),
    ...mapMutations(MAPVIEW_PREFIX, {
      setHoveredComplexId: SET_HOVERED_COMPLEX_ID
    }),
    setBuildingsBelongingToComplex() {
      const allBuildingsSource = this.$store.map.getSource('source-building')
      const allBuildings = allBuildingsSource._data.features
      const buildingsForComplex = allBuildings.filter(
        (buildings) => this.hoveredComplexId === buildings.properties.complexId
      )
      buildingsForComplex.forEach((building) => {
        this.buildingsBelongingToComplex.push(building.properties)
      })
    },
    handleMouseEnterComplex() {
      if (!this.isMeasurementActive) {
        this.map.getCanvas().style.cursor = 'pointer'
        return
      }
    },
    handleMouseMoveComplex(event) {
      if (this.hoveredComplexId !== event.features[0].properties.belongsToId) {
        this.handleMouseLeaveComplex()
      }
      if (this.isMeasurementActive || !this.isFirstLayer(event)) {
        this.handleMouseLeaveComplex()
        return
      }
      this.map.getCanvas().style.cursor = 'pointer'
      this.updateHoverComplex(event)
    },
    handleMouseLeaveComplex() {
      if (this.isMeasurementActive) return
      this.map.getCanvas().style.cursor = 'default'
      this.$emit('hover', '')
      this.hoveredComplexId = ''
      this.setHoveredComplexId(this.hoveredComplexId)
      this.buildingsBelongingToComplex = []
      this.popup.remove()
    },
    updateHoverComplex(event) {
      const features = event.features
      if (
        features &&
        features.length > 0 &&
        this.hoveredComplexId !== features[0].properties.belongsToId
      ) {
        this.popupTitle = features[0].properties.title
        this.$emit('hover', features[0].properties.belongsToId)
        this.showPopup(features)
        this.hoveredComplexId = features[0].properties.belongsToId
        this.setHoveredComplexId(this.hoveredComplexId)
        this.setBuildingsBelongingToComplex()
      }
    },
    isFirstLayer(event) {
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      return renderedFeatures[0].layer.id === this.layerId
    },
    showPopup(features) {
      if (!this.$refs.popup) return
      const coordinates = features[0].geometry.coordinates
      this.popup.setLngLat(coordinates).setDOMContent(this.$refs.popup.$el).addTo(this.$store.map)
    },
    onComplexClicked(event) {
      if (this.isMeasurementActive || !this.isFirstLayer(event)) return
      const complexId = event.features[0].properties.belongsToId
      if (!complexId) return
      this.dispatchComplex({ complex: event.features[0].properties })
      this.commitClick({ type: 'complex' })

      zoomToEntities([complexId])
    }
  }
}
</script>
